import {
  ELECTRIC_VEHICLES_TYPES,
  HANDOVER_CARS_SIDE,
  // SUBSCRIPTIONS_TYPES,
  RESERVATIONS_TYPES,
  AVAILABILITY_STATUSES,
  FILTER_RULE_ENUMS,
  FILTER_OPERATOR_ENUMS,
  DELIVERY_INFO_STATUSES_ENUM,
  SUBSCRIPTIONS_STATUSES_ENUM,
} from '@constants';
import {
  formatDate,
  formatAmount,
  formatVehicleType,
  formatVehicleStage,
  timeFromNow,
  duration,
  formatAddress,
  parseISODuration,
  generateShortId,
  formatCurrencyValue,
  getCleanedUrlParams,
} from '@utils';
import {
  getSubscriptionsRequestPayload,
  getSubscriptionsQueryParams,
  BOARD_META_BY_ID,
} from '@helpers';
import { AVAILABLE_HISTORY_ITEMS } from 'components/common/InstaTaskHistory/constants';
import { addMonths, format, isValid } from 'date-fns';
import { omit, isNil, capitalize } from 'lodash-es';
import { getSubscriptionBundleColorStatus } from 'components/subscriptions/SubscriptionBundles/SubscriptionBundleCard/subscriptionBundleCard.styles';
import { URLS } from 'layouts/DrawerLayout/constants';
import { SUBSCRIPTION_BUNDLES_PRIORITY_MAP } from 'components/subscriptions/constants';
import { ACCEPTABLE_SUBSCRIPTION_AGENT_ROLES } from './constants';

export const getHumanReadableDuration = (
  iso8601duration,
  normalize = false
) => {
  const parsedDuration = parseISODuration(iso8601duration, normalize);

  const { years, months } = parsedDuration;

  let result = years ? `${years} Year${years > 1 ? 's' : ''} ` : '';
  result = months
    ? `${result}${result ? 'and ' : ''}${months} Month${months > 1 ? 's' : ''} `
    : result;

  return result;
};

export const _estimatedDurations = () => {
  const options = [];
  for (let i = 0; i < 81; i++) {
    const text = getHumanReadableDuration(`P${i}M`, true);
    options.push({ value: `P${i}M`, text });
  }
  return options;
};

export const _compute19thMonth = (date) => {
  return format(addMonths(new Date(date), 19), 'MM yyyy');
};

const handleLinkedItems = (array, assigneesMap) => {
  if (!array) {
    return [];
  }
  return array?.map((item) => ({
    board: item?.board || '-',
    id: item?.ticketId || '-',
    entityId: item?.entityId || '-',
    type: item?.type || '-',
    status: item?.status || '-',
    vehicle: item?.vehicle || '-',
    plate: item?.plate || '-',
    assignee: assigneesMap[item?.assignee]?.display || '-',
    createdAt: item?.createdAt
      ? formatDate(item?.createdAt, 'dd/MM/yyyy')
      : '-',
  }));
};

export const handleSubscriptionSteps = (steps) => {
  let frontStep = steps?.vehicleChecks
    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.FRONT;
    })
    ?.map((side) => {
      return {
        title: 'Front Side',
        checkList: [
          {
            title: 'Car Hood',
            value: side?.checklist?.carHood,
          },
          {
            title: 'Front Bumper',
            value: side?.checklist?.frontBumper,
          },
          {
            title: 'Wind Screen',
            value: side?.checklist?.windscreen,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!frontStep) {
    frontStep = {
      title: 'Front Side',
      checkList: [
        {
          title: 'Car Hood',
          value: false,
        },
        {
          title: 'Front Bumper',
          value: false,
        },
        {
          title: 'Wind Screen',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  let backStep = steps?.vehicleChecks
    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.BACK;
    })
    ?.map((side) => {
      return {
        title: 'Back Side',
        checkList: [
          {
            title: 'Back Bumper',
            value: side?.checklist?.backBumper,
          },
          {
            title: 'Car Hood',
            value: side?.checklist?.carHood,
          },
          {
            title: 'Wind Screen',
            value: side?.checklist?.windscreen,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!backStep) {
    backStep = {
      title: 'Back Side',
      checkList: [
        {
          title: 'Back Bumper',
          value: false,
        },
        {
          title: 'Car Hood',
          value: false,
        },
        {
          title: 'Wind Screen',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  let topStep = steps?.vehicleChecks
    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.ABOVE;
    })
    ?.map((side) => {
      return {
        title: 'Top Side',
        checkList: [
          {
            title: 'Top',
            value: side?.checklist?.top,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!topStep) {
    topStep = {
      title: 'Top Side',
      checkList: [
        {
          title: 'Top',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  let leftStep = steps?.vehicleChecks
    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.LEFT;
    })
    ?.map((side) => {
      return {
        title: 'Left Side',
        checkList: [
          {
            title: 'Back Door',
            value: side?.checklist?.backDoor,
          },
          {
            title: 'Back Wheel',
            value: side?.checklist?.backWheel,
          },
          {
            title: 'Front Door',
            value: side?.checklist?.frontDoor,
          },
          {
            title: 'Front Wheel',
            value: side?.checklist?.frontWheel,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!leftStep) {
    leftStep = {
      title: 'Left Side',
      checkList: [
        {
          title: 'Back Door',
          value: false,
        },
        {
          title: 'Back Wheel',
          value: false,
        },
        {
          title: 'Front Door',
          value: false,
        },
        {
          title: 'Front Wheel',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  let rightStep = steps?.vehicleChecks
    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.RIGHT;
    })
    ?.map((side) => {
      return {
        title: 'Right Side',
        checkList: [
          {
            title: 'Back Door',
            value: side?.checklist?.backDoor,
          },
          {
            title: 'Back Wheel',
            value: side?.checklist?.backWheel,
          },
          {
            title: 'Front Door',
            value: side?.checklist?.frontDoor,
          },
          {
            title: 'Front Wheel',
            value: side?.checklist?.frontWheel,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!rightStep) {
    rightStep = {
      title: 'Right Side',
      checkList: [
        {
          title: 'Back Door',
          value: false,
        },
        {
          title: 'Back Wheel',
          value: false,
        },
        {
          title: 'Front Door',
          value: false,
        },
        {
          title: 'Front Wheel',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  let interiorStep = steps?.vehicleChecks

    ?.filter((step) => {
      return step.side === HANDOVER_CARS_SIDE.INTERIOR;
    })
    ?.map((side) => {
      return {
        title: 'Interior',
        checkList: [
          {
            title: 'Auto Show',
            value: side?.checklist?.autoShow,
          },
          {
            title: 'Floor Mats',
            value: side?.checklist?.floorMats,
          },
          {
            title: 'Seats',
            value: side?.checklist?.seats,
          },
          {
            title: 'Top',
            value: side?.checklist?.top,
          },
        ],
        comments: side?.comments,
        images: side?.images,
      };
    })
    .pop();

  if (!interiorStep) {
    interiorStep = {
      title: 'Interior',
      checkList: [
        {
          title: 'Auto Show',
          value: false,
        },
        {
          title: 'Floor Mats',
          value: false,
        },
        {
          title: 'Seats',
          value: false,
        },
        {
          title: 'Top',
          value: false,
        },
      ],
      comments: '',
      images: [],
    };
  }

  const observationsStep = {
    title: 'Additional Observations',
    comments: steps?.extraNotes?.comments || '',
    images: steps?.extraNotes?.images || [],
  };

  const data = [];
  if (frontStep) {
    data?.push(frontStep);
  }
  if (leftStep) {
    data?.push(leftStep);
  }
  if (backStep) {
    data?.push(backStep);
  }
  if (rightStep) {
    data?.push(rightStep);
  }
  if (topStep) {
    data?.push(topStep);
  }
  if (interiorStep) {
    data?.push(interiorStep);
  }
  if (observationsStep) {
    data?.push(observationsStep);
  }

  return data;
};

export const handleSubscription = (
  subscription,
  linkedItems,
  linkedItemsTotal,
  reasonsOfReturningEnum,
  deliveryStatuses,
  driverAgents,
  assigneesMap,
  servicePointsMap,
  outOfSlaOptions
) => {
  let owner = {};
  const data = { ...subscription };

  const ruleLength = data?.rules?.length;
  let activeRule = null;
  const vehicle = data?.vehicle;

  const reasonsOfReturning = (data?.reasonsOfReturning || [])
    .map((reasonId) => {
      const reason = reasonsOfReturningEnum.find(
        (option) => Number(option.id) === Number(reasonId)
      );
      return reason ? { text: reason.name, value: reason.id } : null;
    })
    .filter(Boolean);

  const deliveryStatus = deliveryStatuses.find(
    (status) => Number(status?.id) === Number(data?.deliveryInfo?.status)
  );

  const assignee = assigneesMap?.[data?.assignee] || {
    text: data?.assignee || 'None',
    value: data?.assignee || 'none',
  };

  const outOfSla =
    outOfSlaOptions?.filter((item) => data?.outOfSla?.includes(item.id)) || [];

  const getAgentName = (email) => {
    const agent = driverAgents?.find((ag) => ag.value === email);
    return agent ? agent.text : '-';
  };

  if (ruleLength) {
    activeRule = data?.rules?.[ruleLength - 1];
  }
  data.assignee = assignee;
  data.activeRule = activeRule;
  data.amount = activeRule?.amount;
  data.isErfuddAlert = subscription?.isErfuddAlert || false;
  data.promisedReturnDateValue =
    data?.promisedReturnDate &&
    data?.promisedReturnDate !== '0001-01-01T00:00:00Z'
      ? formatDate(data?.promisedReturnDate, 'dd/MM/yyyy HH:mm')
      : null;
  data.reasonsOfReturning = reasonsOfReturning;
  data.reasonsOfReturningOptions = reasonsOfReturningEnum.map((reason) => ({
    text: reason.name,
    value: reason.id,
  }));
  data.customerTypes =
    data?.customerTypes?.map((type) => ({
      text: type.name,
      value: type.key,
    })) || [];
  if (activeRule) {
    activeRule.fees = activeRule?.fees?.map((fee) => {
      fee.formattedAmount = fee?.amount
        ? formatAmount(fee?.amount?.value)
        : '-';
      fee.createdAt = fee?.createdAt
        ? formatDate(fee?.createdAt, 'dd/MM/yyyy')
        : '-';
      fee.updatedAt = fee?.updatedAt
        ? formatDate(fee?.updatedAt, 'dd/MM/yyyy')
        : '-';
      fee.status = fee?.status || '-';
      fee.invoices = fee.invoices || '-';
      return fee;
    });
  }
  data.subscription = {
    billingPeriod: {
      label: 'Billing Period:',
      renderedValue: activeRule?.billing?.period
        ? getHumanReadableDuration(activeRule?.billing?.period, true)
        : '-',
      onEditField: null,
    },
    scheduledByMonthDay: {
      label: 'Payment Due Date:',
      renderedValue: activeRule?.billing?.scheduledByMonthDay || '-',
      onEditField: null,
    },
    month: {
      canEdited: false,
      label: '19th Month:',
      renderedValue:
        data?.deliveryDate && data?.deliveryDate !== '0001-01-01T00:00:00Z'
          ? _compute19thMonth(data?.deliveryDate)
          : '-',
      onEditField: null,
    },
    promisedDeliveryDate: {
      canEdited: true,
      type: 'dateTimeRange',
      label: 'Promised Delivery Date Range:',
      startProp: 'promisedDeliveryDate',
      endProp: 'promisedDeliveryDateUntil',
      renderedValue: (() => {
        const startDate = data?.promisedDeliveryDate
          ? formatDate(data?.promisedDeliveryDate, 'dd/MM/yyyy HH:mm')
          : '';
        const endDate = data?.promisedDeliveryDateUntil
          ? formatDate(data?.promisedDeliveryDateUntil, 'dd/MM/yyyy HH:mm')
          : '';

        return `${startDate} - ${endDate}`;
      })(),
      defaultValue: {
        promisedDeliveryDate: data?.promisedDeliveryDate || '',
        promisedDeliveryDateUntil: data?.promisedDeliveryDateUntil || '',
      },
      rules: {
        validate: {
          startDateRequired: (value) => {
            return !!value?.promisedDeliveryDate;
          },
          endDateRequired: (value) => {
            return !!value?.promisedDeliveryDateUntil;
          },
        },
      },
      onEditField: null,
    },
    outOfSla: {
      canEdited: true,
      label: 'Out Of SLA:',
      type: 'multiselect',
      placeholder: '',
      defaultValue:
        outOfSla?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [],
      options:
        outOfSlaOptions?.map((item) => ({
          value: item.id,
          text: item.name,
        })) || [],
      renderedValue: outOfSla?.map((item) => item.name).join(', ') || '-',
      onEditField: null,
      fieldWrapperStyle: {
        width: '100%',
      },
      minify: 1,
      hasCheckboxes: true,
    },
    promisedReturnDate: {
      canEdited: true,
      isClearable: true,
      type: 'dateTime',
      label: 'Promised Return Date:',
      renderedValue:
        data?.promisedReturnDate &&
        data?.promisedReturnDate !== '0001-01-01T00:00:00Z'
          ? formatDate(data?.promisedReturnDate, 'dd/MM/yyyy HH:mm')
          : '-',
      defaultValue:
        data?.promisedReturnDate !== '0001-01-01T00:00:00Z'
          ? data?.promisedReturnDate
          : null,
      onEditField: null,
    },
    duration: {
      canEdited: true,
      label: 'Contract Duration:',
      type: 'dropdown',
      placeholder: '',
      defaultValue: activeRule?.estimatedDuration,
      options: _estimatedDurations(),
      renderedValue: activeRule?.estimatedDuration
        ? getHumanReadableDuration(activeRule?.estimatedDuration, true)
        : '-',
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    startContractDate: {
      canEdited: true,
      isClearable: true,
      type: 'dateTime',
      label: 'Start Contract Date:',
      renderedValue:
        data?.startContractDate &&
        data?.startContractDate !== '0001-01-01T00:00:00Z'
          ? formatDate(data?.startContractDate, 'dd/MM/yyyy HH:mm')
          : '-',
      defaultValue:
        data?.startContractDate !== '0001-01-01T00:00:00Z'
          ? data?.startContractDate
          : null,
      onEditField: null,
    },
    endContractDate: {
      canEdited: false,
      label: 'End Contract Date:',
      type: 'date',
      placeholder: '',
      renderedValue:
        data?.endContractDate &&
        data?.endContractDate !== '0001-01-01T00:00:00Z'
          ? formatDate(data?.endContractDate, 'dd/MM/yyyy HH:mm')
          : '-',
      defaultValue:
        data?.endContractDate &&
        data?.endContractDate !== '0001-01-01T00:00:00Z'
          ? formatDate(data?.endContractDate, 'dd/MM/yyyy HH:mm')
          : '-',
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    durationFromDeliveryDate: {
      canEdited: false,
      label: 'Duration:',
      type: 'dropdown',
      placeholder: '',
      defaultValue: '',
      renderedValue:
        // eslint-disable-next-line no-nested-ternary
        !data?.returnDate && !!data?.deliveryDate
          ? timeFromNow(data?.deliveryDate)
          : !!data?.deliveryDate && !!data?.returnDate
          ? duration(data?.deliveryDate, data?.returnDate)
          : '-',
      onEditField: null,
      rules: {
        required: '* This field is required',
      },
      fieldWrapperStyle: {
        width: '100%',
      },
    },
    outstandingChargesDate: {
      canEdited: true,
      type: 'date',
      label: 'Outstanding Charges Date:',
      renderedValue:
        data?.outstandingChargesDate &&
        data?.outstandingChargesDate !== '0001-01-01T00:00:00Z'
          ? formatDate(data?.outstandingChargesDate, 'dd/MM/yyyy')
          : '-',
      defaultValue: data?.outstandingChargesDate,
    },
    outstandingChargesTotalAmount: {
      canEdited: true,
      type: 'number',
      label: 'Outstanding Charges Total Amount:',
      renderedValue: data?.outstandingChargesTotalAmount || '-',
      defaultValue: data?.outstandingChargesTotalAmount,
    },
    contractValue: {
      type: 'text',
      label: 'Contract Value:',
      renderedValue: data?.contractValue?.number || '-',
      defaultValue: data?.contractValue?.number,
    },
    marketingCampaign: {
      type: 'text',
      label: 'Marketing Campaign:',
      renderedValue: data?.marketingCampaignId || '-',
      defaultValue: data?.marketingCampaignId,
    },
    couponDiscount: {
      type: 'text',
      label: 'Coupon Discount:',
      renderedValue: data?.couponDiscount || '-',
      defaultValue: data?.couponDiscount,
    },
  };
  data.vehicleInformation = {
    vehicle: {
      label: 'Vehicle:',
      renderedValue: `${vehicle?.manufacturer} - ${vehicle?.model}`,
      link: `/vehicles/${vehicle?.id}`,
    },
    type: {
      label: 'Type:',
      renderedValue: vehicle?.sku?.type
        ? formatVehicleType(vehicle?.sku?.type?.toUpperCase())
        : '-',
    },
    plate: {
      label: 'Plate:',
      renderedValue: vehicle?.plate || '-',
    },
    carId: {
      label: 'Car ID:',
      renderedValue: vehicle?.internalId || '-',
    },
    createdAt: {
      label: 'Created:',
      renderedValue: vehicle?.createdAt
        ? formatDate(vehicle?.createdAt, 'dd/MM/yyyy')
        : '-',
    },
    transmission: {
      label: 'Transmission:',
      renderedValue: vehicle?.properties?.transmission || '-',
    },
    color: {
      label: 'Color:',
      renderedValue: vehicle?.properties?.color || '-',
    },
    fuelType: {
      label: 'Fuel Type:',
      renderedValue: vehicle?.properties?.fuelType || '-',
    },
    stage: {
      label: 'Stage:',
      renderedValue: formatVehicleStage(vehicle?.procurement?.currentStageId),
    },
    location: {
      label: 'Location:',
      renderedValue:
        servicePointsMap[vehicle?.location?.name]?.name ||
        vehicle?.location?.name ||
        '-',
    },
  };
  data.userLanguage = 'el';
  data.subscribers = data.subscribers?.map((subscriber) => {
    subscriber.fullName = `${subscriber?.firstName || ''} ${
      subscriber.lastName || ''
    }`;
    const currentSubscription = subscriber?.subscriptions
      ?.filter((item) => {
        return item?.id === data?.id;
      })
      ?.pop();

    subscriber.activeDriver = currentSubscription?.activeDriver;

    subscriber.type = currentSubscription?.type;
    subscriber.role = currentSubscription?.role;
    subscriber.createdAt = subscriber?.createdAt
      ? formatDate(subscriber?.createdAt, 'dd/MM/yyyy')
      : '-';
    subscriber.updatedAt = subscriber?.createdAt
      ? formatDate(subscriber?.updatedAt, 'dd/MM/yyyy')
      : '-';

    if (currentSubscription) {
      owner = subscriber;
      owner.type = currentSubscription?.type;
    }
    if (currentSubscription?.role === 1) {
      data.userLanguage = subscriber?.lang || 'el';
    }
    return subscriber;
  });
  // TODO: comment out when contact person gets unblocked
  // .filter(
  //   (subscriber) => subscriber.role !== SUBSCRIBER_ROLES.contactPerson.text
  // );

  data.individualBillingInfo = null;
  data.businessBillingInfo = null;

  if (
    !data?.billingInfo?.firstName &&
    !data?.billingInfo?.lastName &&
    !data?.billingInfo?.vat &&
    !data?.billingInfo?.companyName &&
    !data?.billingInfo?.doi &&
    !data?.billingInfo?.companyName &&
    !data?.billingInfo?.doi &&
    !data?.billingInfo?.activity &&
    !data?.billingInfo?.phoneNumber &&
    !data?.billingInfo?.email &&
    !data?.billingInfo?.address &&
    !data?.billingInfo?.postalCode
  ) {
    data.emptyBillingInfo = {
      contract: {
        label: 'Contract:',
        type: 'dropdown',
        placeholder: '',
        defaultValue: 'Ιδιώτης',
        options: [
          {
            text: 'Ιδιώτης',
            value: 'Ιδιώτης',
          },
          {
            text: 'Εταιρεία',
            value: 'Εταιρεία',
          },
        ],
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hiddenToJSONList: true,
      },
      firstName: {
        label: 'First Name:',
        renderedValue: data?.billingInfo?.firstName || '-',
        defaultValue: data?.billingInfo?.firstName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      lastName: {
        label: 'Last Name:',
        renderedValue: data?.billingInfo?.lastName || '-',
        defaultValue: data?.billingInfo?.lastName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      vat: {
        label: 'Tax code:',
        renderedValue: data?.billingInfo?.vat || '-',
        defaultValue: data?.billingInfo?.vat,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      companyName: {
        label: 'Business Name:',
        renderedValue: data?.billingInfo?.companyName || '-',
        defaultValue: data?.billingInfo?.companyName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: true,
        hiddenToJSONList: true,
      },
      doi: {
        label: 'D.O.Y:',
        renderedValue: data?.billingInfo?.doi || '-',
        defaultValue: data?.billingInfo?.doi,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      activity: {
        label: 'Activity:',
        renderedValue: data?.billingInfo?.activity || '-',
        defaultValue: data?.billingInfo?.activity,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      phoneNumber: {
        label: 'Phone Number:',
        renderedValue: data?.billingInfo?.phoneNumber || '-',
        defaultValue: data?.billingInfo?.phoneNumber,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      email: {
        label: 'E-mail:',
        renderedValue: data?.billingInfo?.email || '-',
        defaultValue: data?.billingInfo?.email,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
          pattern: {
            value: `${/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i}`,
            message: 'invalid email address',
          },
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      address: {
        label: 'Street Address:',
        renderedValue: data?.billingInfo?.address || '-',
        defaultValue: data?.billingInfo?.address,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      postalCode: {
        label: 'Postal Code:',
        renderedValue: data?.billingInfo?.postalCode || '-',
        defaultValue: data?.billingInfo?.postalCode,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
    };
  } else if (data?.billingInfo?.companyName) {
    data.businessBillingInfo = {
      firstName: {
        label: 'First Name:',
        renderedValue: data?.billingInfo?.firstName || '-',
        defaultValue: data?.billingInfo?.firstName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      lastName: {
        label: 'Last Name:',
        renderedValue: data?.billingInfo?.lastName || '-',
        defaultValue: data?.billingInfo?.lastName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      vat: {
        label: 'Tax code:',
        renderedValue: data?.billingInfo?.vat || '-',
        defaultValue: data?.billingInfo?.vat,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
        // TODO: disable it when we have a permission logic
        // disabled: !!data?.billingInfo?.vat,
      },
      companyName: {
        label: 'Business Name:',
        renderedValue: data?.billingInfo?.companyName || '-',
        defaultValue: data?.billingInfo?.companyName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      doi: {
        label: 'D.O.Y:',
        renderedValue: data?.billingInfo?.doi || '-',
        defaultValue: data?.billingInfo?.doi,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      activity: {
        label: 'Activity:',
        renderedValue: data?.billingInfo?.activity || '-',
        defaultValue: data?.billingInfo?.activity,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      phoneNumber: {
        label: 'Phone Number:',
        renderedValue: data?.billingInfo?.phoneNumber || '-',
        defaultValue: data?.billingInfo?.phoneNumber,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      email: {
        label: 'E-mail:',
        renderedValue: data?.billingInfo?.email || '-',
        defaultValue: data?.billingInfo?.email,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
          pattern: {
            value: `${/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i}`,
            message: 'invalid email address',
          },
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      address: {
        label: 'Street Address:',
        renderedValue: data?.billingInfo?.address || '-',
        defaultValue: data?.billingInfo?.address,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      postalCode: {
        label: 'Postal Code:',
        renderedValue: data?.billingInfo?.postalCode || '-',
        defaultValue: data?.billingInfo?.postalCode,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
    };
  } else {
    data.individualBillingInfo = {
      firstName: {
        label: 'First Name:',
        renderedValue: data?.billingInfo?.firstName || '-',
        defaultValue: data?.billingInfo?.firstName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      lastName: {
        label: 'Last Name:',
        renderedValue: data?.billingInfo?.lastName || '-',
        defaultValue: data?.billingInfo?.lastName,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      vat: {
        label: 'Tax code:',
        renderedValue: data?.billingInfo?.vat || '-',
        defaultValue: data?.billingInfo?.vat,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
        // TODO: disable it when we have a permission logic
        // disabled: !!data?.billingInfo?.vat,
      },
      doi: {
        label: 'D.O.Y:',
        renderedValue: data?.billingInfo?.doi || '-',
        defaultValue: data?.billingInfo?.doi,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      activity: {
        label: 'Activity:',
        renderedValue: data?.billingInfo?.activity || 'Ιδιώτης',
        defaultValue: data?.billingInfo?.activity || 'Ιδιώτης',
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      phoneNumber: {
        label: 'Phone Number:',
        renderedValue: data?.billingInfo?.phoneNumber || '-',
        defaultValue: data?.billingInfo?.phoneNumber,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      address: {
        label: 'Street Address:',
        renderedValue: data?.billingInfo?.address || '-',
        defaultValue: data?.billingInfo?.address,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      email: {
        label: 'E-mail:',
        renderedValue: data?.billingInfo?.email || '-',
        defaultValue: data?.billingInfo?.email,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
          pattern: {
            value: `${/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i}`,
            message: 'invalid email address',
          },
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
      postalCode: {
        label: 'Postal Code:',
        renderedValue: data?.billingInfo?.postalCode || '-',
        defaultValue: data?.billingInfo?.postalCode,
        onEditField: null,
        canEdited: true,
        type: 'text',
        placeholder: '',
        rules: {
          required: '* This field is required',
        },
        fieldWrapperStyle: {
          width: '100%',
        },
        hidden: false,
      },
    };
  }

  data.deliveryInfoFinalChecks = [
    {
      insuranceContract: {
        title: 'Insurance Contract',
        value: data?.deliveryInfo?.finalChecklist?.insuranceContract,
      },
      copyOfTheVehiclesLicense: {
        title: `Copy of the Vehicle's License`,
        value: data?.deliveryInfo?.finalChecklist?.copyOfTheVehiclesLicense,
      },
      instakit: {
        title: 'instakit',
        value: data?.deliveryInfo?.finalChecklist?.instakit,
      },
      pharmacyWarningTriangle: {
        title: `Pharmacy Lit & Warning Triangle`,
        value: data?.deliveryInfo?.finalChecklist?.pharmacyWarningTriangle,
      },
      fireExtinguisher: {
        title: 'Fire Extinguisher',
        value: data?.deliveryInfo?.finalChecklist?.fireExtinguisher,
      },
      foam: {
        title: `Foam`,
        value: data?.deliveryInfo?.finalChecklist?.foam,
      },
      spareTire: {
        title: 'Spare Tire',
        value: data?.deliveryInfo?.finalChecklist?.spareTire,
      },
      carMats: {
        title: `Car Mats`,
        value: data?.deliveryInfo?.finalChecklist?.carMats,
      },
      shelf: {
        title: 'Shelf',
        value: data?.deliveryInfo?.finalChecklist?.shelf,
      },
      skidChain: {
        title: `Skid Chain`,
        value: data?.deliveryInfo?.finalChecklist?.skidChain,
      },
      secondKey: {
        title: `Second Key`,
        value: data?.deliveryInfo?.finalChecklist?.secondKey,
      },
      ...(ELECTRIC_VEHICLES_TYPES?.includes(
        data?.vehicle?.sku?.engine?.type?.toLowerCase()
      )
        ? {
            chargingCable: {
              title: 'Charging Cable',
              value: data?.deliveryInfo?.finalChecklist?.chargingCable,
            },
          }
        : {}),
    },
  ];

  data.returnInfoFinalChecks = [
    {
      instakit: {
        title: 'instakit',
        value: data?.returnInfo?.finalChecklist?.instakit,
      },
      pharmacyWarningTriangle: {
        title: `Pharmacy Lit & Warning Triangle`,
        value: data?.returnInfo?.finalChecklist?.pharmacyWarningTriangle,
      },
      fireExtinguisher: {
        title: 'Fire Extinguisher',
        value: data?.returnInfo?.finalChecklist?.fireExtinguisher,
      },
      foam: {
        title: `Foam`,
        value: data?.returnInfo?.finalChecklist?.foam,
      },
      spareTire: {
        title: 'Spare Tire',
        value: data?.returnInfo?.finalChecklist?.spareTire,
      },
      carMats: {
        title: `Car Mats`,
        value: data?.returnInfo?.finalChecklist?.carMats,
      },
      shelf: {
        title: 'Shelf',
        value: data?.returnInfo?.finalChecklist?.shelf,
      },
      skidChain: {
        title: `Skid Chain`,
        value: data?.returnInfo?.finalChecklist?.skidChain,
      },
      secondKey: {
        title: `Second Key`,
        value: data?.returnInfo?.finalChecklist?.secondKey,
      },
      ...(ELECTRIC_VEHICLES_TYPES?.includes(
        data?.vehicle?.sku?.engine?.type?.toLowerCase()
      )
        ? {
            chargingCable: {
              title: 'Charging Cable',
              value: data?.returnInfo?.finalChecklist?.chargingCable,
            },
          }
        : {}),
    },
  ];

  data.deliveryInfoVehicleDetails = [
    {
      title: '',
      fields: [
        {
          title: 'Initial Subscription ID:',
          renderedValue: data?.initialSubscriptionId || '-',
          value: data?.initialSubscriptionId,
          canEdited: true,
          key: 'initialSubscriptionId',
        },
        {
          title: 'Delivery Status:',
          renderedValue: deliveryStatus?.name || '-',
        },
        {
          title: 'Promised Delivery Date Range:',
          renderedValue:
            data?.promisedDeliveryDate && data?.promisedDeliveryDateUntil
              ? `${formatDate(
                  data?.promisedDeliveryDate,
                  'dd/MM/yyyy HH:mm'
                )} - ${formatDate(
                  data?.promisedDeliveryDateUntil,
                  'dd/MM/yyyy HH:mm'
                )}`
              : '-',
        },
        {
          title: 'Delivery Date:',
          renderedValue:
            data?.deliveryDate && data?.deliveryDate !== '0001-01-01T00:00:00Z'
              ? formatDate(data?.deliveryDate, 'dd/MM/yyyy HH:mm')
              : '-',
        },
        {
          title: 'Delivery Point:',
          renderedValue: data?.deliveryInfo?.location || '-',
        },
        {
          title: 'Delivery Address:',
          renderedValue: formatAddress(data?.deliveryInfo?.address) || '-',
        },
        {
          title: 'Delivered To:',
          renderedValue: data?.deliveryInfo?.userName || '-',
        },
        {
          title: 'Assignee:',
          renderedValue: getAgentName(data?.deliveryInfo?.assignee),
        },
        {
          title: 'Vehicle Parking Address:',
          renderedValue: data?.deliveryInfo?.vehicleParkingAddress?.id
            ? formatAddress(data?.deliveryInfo?.vehicleParkingAddress)
            : '-',
        },
        {
          title: "Driver's Locker PIN:",
          renderedValue:
            data?.deliveryInfo?.boxnowInfo?.lockerInternalPin || '-',
        },
        {
          title: "User's Locker PIN:",
          renderedValue: data?.deliveryInfo?.boxnowInfo?.lockerUserPin || '-',
        },
        {
          title: `Km:`,
          renderedValue: data?.deliveryInfo?.kmFuelTiresCheck?.km || '-',
          value: data?.deliveryInfo?.kmFuelTiresCheck?.km,
          canEdited: true,
          key: 'km',
        },
        {
          title: 'Fuel:',
          key: 'fuel',
          renderedValue: data?.deliveryInfo?.kmFuelTiresCheck?.fuel || '-',
          value: data?.deliveryInfo?.kmFuelTiresCheck?.fuel,
        },
      ],
    },
    {
      title: `Tires`,
      fields: [
        {
          title: 'Position:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[0]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[0]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[0]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[1]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[1]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[1]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[2]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[2]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[2]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[3]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[3]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.deliveryInfo?.kmFuelTiresCheck?.tires?.[3]?.dot || '-',
          key: generateShortId(),
        },
      ],
    },
  ];

  data.returnInfoVehicleDetails = [
    {
      title: '',
      fields: [
        {
          title: 'Promised Return Date:',
          renderedValue:
            data?.promisedReturnDate &&
            data?.promisedReturnDate !== '0001-01-01T00:00:00Z'
              ? formatDate(data?.promisedReturnDate, 'dd/MM/yyyy HH:mm')
              : '-',
        },

        {
          title: 'Return Date:',
          renderedValue:
            data?.returnDate && data?.returnDate !== '0001-01-01T00:00:00Z'
              ? formatDate(data?.returnDate, 'dd/MM/yyyy HH:mm')
              : '-',
        },
        {
          title: 'Return Point:',
          renderedValue: data?.returnInfo?.location || '-',
        },
        {
          title: 'Return Address:',
          renderedValue: formatAddress(data?.returnInfo?.address) || '-',
        },
        {
          title: 'Returned From:',
          renderedValue: data?.returnInfo?.userName || '',
        },
        {
          title: 'Assignee:',
          renderedValue: getAgentName(data?.returnInfo?.assignee),
        },
        {
          title: `Km:`,
          renderedValue: data?.returnInfo?.kmFuelTiresCheck?.km || '-',
        },
        {
          title: 'Fuel:',
          key: 'fuel',
          renderedValue: data?.returnInfo?.kmFuelTiresCheck?.fuel || '-',
          value: data?.returnInfo?.kmFuelTiresCheck?.fuel,
        },
      ],
    },
    {
      title: `Tires`,
      fields: [
        {
          title: 'Position:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[0]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[0]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[0]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[1]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[1]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[1]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[2]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[2]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[2]?.dot || '-',
          key: generateShortId(),
        },
        {
          title: 'Position:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[3]?.position || '-',
          key: generateShortId(),
        },
        {
          title: 'Dimensions:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[3]?.dimensions || '-',
          key: generateShortId(),
        },
        {
          title: 'DOT:',
          renderedValue:
            data?.returnInfo?.kmFuelTiresCheck?.tires?.[3]?.dot || '-',
          key: generateShortId(),
        },
      ],
    },
  ];

  data.deliveryInspectionInfoDetails = [
    {
      title: 'Delivery Inspection Info',
      fields: [
        {
          title: 'Inspection Date',
          renderedValue: data?.deliveryInspectionInfo?.date
            ? formatDate(data?.deliveryInspectionInfo?.date, 'dd/MM/yyyy HH:mm')
            : '-',
        },
        {
          title: 'Inspection Point',
          renderedValue: data?.deliveryInspectionInfo?.point || '-',
        },
        {
          title: 'Assignee',
          renderedValue: getAgentName(data?.deliveryInspectionInfo?.assigneeId),
        },
      ],
    },
  ];

  data.returnInspectionInfoDetails = [
    {
      title: 'Return Inspection Info',
      fields: [
        {
          title: 'Inspection Date',
          renderedValue: data?.returnInspectionInfo?.date
            ? formatDate(data?.returnInspectionInfo?.date, 'dd/MM/yyyy HH:mm')
            : '-',
        },
        {
          title: 'Inspection Point',
          renderedValue: data?.returnInspectionInfo?.point || '-',
        },
        {
          title: 'Assignee',
          renderedValue: getAgentName(data?.returnInspectionInfo?.assigneeId),
        },
      ],
    },
  ];

  data.deliveryInfoSides = handleSubscriptionSteps(data?.deliveryInfo);
  data.returnInfoSides = handleSubscriptionSteps(data?.returnInfo);

  data.deliveryInfoExtraNotes = data?.deliveryInfo?.extraNotes || {};
  data.returnInfoExtraNotes = data?.returnInfo?.extraNotes || {};
  data.deliveryInfoTireImages =
    data?.deliveryInfo?.kmFuelTiresCheck?.images || [];
  data.returnInfoTireImages = data?.returnInfo?.kmFuelTiresCheck?.images || [];

  data.returnInfoLeasingContract = {
    fileName: data?.returnInfo?.tenancyAgreement?.filename,
    url: data?.returnInfo?.tenancyAgreement?.url,
  };

  data.deliveryInfoLeasingContract = {
    fileName: data?.deliveryInfo?.tenancyAgreement?.filename,
    url: data?.deliveryInfo?.tenancyAgreement?.url,
  };

  data.activeRule = activeRule;
  data.cannotMakeDeliveryReason =
    !data?.billingInfo?.firstName &&
    'Please make sure that the Billing Information of the Subscription has been filled';
  const billingInfoORKmMissing =
    !data?.billingInfo?.firstName ||
    (data?.deliveryInfo?.kmFuelTiresCheck?.km <= 0 &&
      'Please make sure that the Billing Information and the Delivery Km Field of the Subscription has been filled');
  const deliveryDateMissing =
    !data?.deliveryDate &&
    'Please make sure that the Delivery Date of the Subscription has been filled';

  data.cannotMakeReturnReason = billingInfoORKmMissing || deliveryDateMissing;

  data.deliveryInfoAddress = {
    lat: data?.deliveryInfo?.address?.latitude,
    lng: data?.deliveryInfo?.address?.longitude,
    latitude: data?.deliveryInfo?.address?.latitude,
    longitude: data?.deliveryInfo?.address?.longitude,
    streetAddress: data?.deliveryInfo?.address?.streetAddress,
    streetNumber: data?.deliveryInfo?.address?.streetNumber,
    region: data?.deliveryInfo?.address?.region,
    postalCode: data?.deliveryInfo?.address?.postalCode,
    country: data?.deliveryInfo?.address?.country,
    locality: data?.deliveryInfo?.address?.locality,
    location: data?.deliveryInfo?.location,
  };
  data.returnInfoAddress = {
    lat: data?.returnInfo?.address?.latitude,
    lng: data?.returnInfo?.address?.longitude,
    latitude: data?.returnInfo?.address?.latitude,
    longitude: data?.returnInfo?.address?.longitude,
    streetAddress: data?.returnInfo?.address?.streetAddress,
    streetNumber: data?.returnInfo?.address?.streetNumber,
    region: data?.returnInfo?.address?.region,
    postalCode: data?.returnInfo?.address?.postalCode,
    country: data?.returnInfo?.address?.country,
    locality: data?.returnInfo?.address?.locality,
    location: data?.returnInfo?.location,
  };
  const deliveryDetails = [];
  const returnDetails = [];
  data?.subscribers?.forEach((subscriber, index) => {
    if (subscriber.activeDriver) {
      deliveryDetails?.push({
        title: `driver${index + 1}`,
        isSelected: subscriber?.id === data?.deliveryInfo?.userId,
        fields: [
          {
            label: 'name',
            value: `${subscriber?.firstName} ${subscriber?.lastName}`,
          },
          {
            label: 'dob',
            value: subscriber?.dateOfBirth
              ? formatDate(subscriber?.dateOfBirth, 'dd.MM.yyyy')
              : '-',
          },
          {
            label: 'drivingLicense',
            value: subscriber?.driversLicenseNumber || '-',
          },
          {
            label: 'dateOfIssue',
            value: subscriber?.driversLicenseIssueDate
              ? formatDate(subscriber?.driversLicenseIssueDate, 'dd.MM.yyyy')
              : '-',
          },
        ],
      });
      returnDetails?.push({
        title: `driver${index + 1}`,
        isSelected: subscriber?.id === data?.returnInfo?.userId,
        fields: [
          {
            label: 'name',
            value: `${subscriber?.firstName} ${subscriber?.lastName}`,
          },
          {
            label: 'dob',
            value: subscriber?.dateOfBirth
              ? formatDate(subscriber?.dateOfBirth, 'dd.MM.yyyy')
              : '-',
          },
          {
            label: 'drivingLicense',
            value: subscriber?.driversLicenseNumber || '-',
          },
          {
            label: 'dateOfIssue',
            value: subscriber?.driversLicenseIssueDate
              ? formatDate(subscriber?.driversLicenseIssueDate, 'dd.MM.yyyy')
              : '-',
          },
        ],
      });
    }
  });
  deliveryDetails?.push({
    title: 'lessee',
    isSelected: false,
    fields: [
      {
        label: 'name',
        value: data?.billingInfo?.firstName
          ? `${data?.billingInfo?.firstName} ${data?.billingInfo?.lastName}`
          : `${data?.billingInfo?.companyName}`,
      },
      {
        label: 'activity',
        value: data?.billingInfo?.activity || '-',
      },
      {
        label: 'address',
        value: data?.billingInfo?.address || '-',
      },
      {
        label: 'vat',
        value: data?.billingInfo?.vat || '-',
      },
      {
        label: 'doi',
        value: data?.billingInfo?.doi || '-',
      },
    ],
  });
  returnDetails?.push({
    title: 'lessee',
    isSelected: false,
    fields: [
      {
        label: 'name',
        value: data?.billingInfo?.firstName
          ? `${data?.billingInfo?.firstName} ${data?.billingInfo?.lastName}`
          : `${data?.billingInfo?.companyName}`,
      },
      {
        label: 'activity',
        value: data?.billingInfo?.activity || '-',
      },
      {
        label: 'address',
        value: data?.billingInfo?.address || '-',
      },
      {
        label: 'vat',
        value: data?.billingInfo?.vat || '-',
      },
      {
        label: 'doi',
        value: data?.billingInfo?.doi || '-',
      },
    ],
  });
  deliveryDetails?.push({
    title: 'vehicleDetails',
    isSelected: false,
    fields: [
      {
        label: 'plate',
        value: data?.vehicle?.plate || '-',
      },
      {
        label: 'brandModel',
        value: `${data?.vehicle?.manufacturer || '-'} ${data?.vehicle?.model}`,
      },
      {
        label: 'color',
        value: `${data?.vehicle?.properties?.color || '-'}`,
      },
    ],
  });
  returnDetails?.push({
    title: 'vehicleDetails',
    isSelected: false,
    fields: [
      {
        label: 'plate',
        value: data?.vehicle?.plate || '-',
      },
      {
        label: 'brandModel',
        value: `${data?.vehicle?.manufacturer || '-'} ${data?.vehicle?.model}`,
      },
      {
        label: 'color',
        value: `${data?.vehicle?.properties?.color || '-'}`,
      },
    ],
  });
  deliveryDetails?.push({
    title: 'km',
    isSelected: false,
    fields: [
      {
        label: 'delivery',
        value: data?.deliveryInfo?.kmFuelTiresCheck?.km || '-',
      },
    ],
  });
  returnDetails?.push({
    title: 'km',
    isSelected: false,
    fields: [
      {
        label: 'delivery',
        value: data?.returnInfo?.kmFuelTiresCheck?.km || '-',
      },
    ],
  });
  deliveryDetails?.push({
    title: 'fuel',
    isSelected: false,
    fields: [
      {
        label: 'delivery',
        value: data?.deliveryInfo?.kmFuelTiresCheck?.fuel,
      },
    ],
  });
  returnDetails?.push({
    title: 'fuel',
    isSelected: false,
    fields: [
      {
        label: 'delivery',
        value: data?.returnInfo?.kmFuelTiresCheck?.fuel,
      },
    ],
  });
  data?.deliveryInfo?.kmFuelTiresCheck?.tires?.forEach((tire) => {
    deliveryDetails?.push({
      title: 'tires',
      isSelected: false,
      fields: [
        {
          label: 'Position',
          value: tire?.position || '-',
        },
        {
          label: 'dimensions',
          value: tire?.dimensions || '-',
        },
        {
          label: 'DOT',
          value: tire?.dot || '-',
        },
      ],
    });
  });

  data?.returnInfo?.kmFuelTiresCheck?.tires?.forEach((tire) => {
    returnDetails?.push({
      title: 'tires',
      isSelected: false,
      fields: [
        {
          label: 'Position',
          value: tire?.position || '-',
        },
        {
          label: 'dimensions',
          value: tire?.dimensions || '-',
        },
        {
          label: 'DOT',
          value: tire?.dot || '-',
        },
      ],
    });
  });

  data.deliveryInfoDriversDetails = deliveryDetails;
  data.returnInfoDriversDetails = returnDetails;
  data.linkedItems = handleLinkedItems(linkedItems, assigneesMap);
  data.linkedItemsTotal = linkedItemsTotal;
  data.products = data.products?.map((item) => {
    return {
      ...item,
      vat: item?.vat ? item.vat * 100 : item?.vat,
      customFields: {
        ...item?.customFields,
        initialMonthsLength: item?.customFields?.months?.length,
        months: item?.customFields?.months
          ? item?.customFields?.months?.map((month) => {
              return {
                text: `${month}ος`,
                value: month,
              };
            })
          : undefined,
        extraMonths: item?.customFields?.extraMonths
          ? item?.customFields?.extraMonths?.map((month) => {
              return {
                text: `${month}ος`,
                value: month,
              };
            })
          : undefined,
      },
    };
  });
  return data;
};

export const handleServicePoints = (data = []) =>
  data.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

const getBundleSortPriority = (bundleStatus) => {
  return SUBSCRIPTION_BUNDLES_PRIORITY_MAP[bundleStatus?.value] || 5;
};

export const handleSubscriptionsData = (
  data = [],
  deliveryStatuses = [],
  servicePointsMap = {},
  assigneesMap = {},
  outOfSlaOptions = []
) => {
  const deliveryStatus = (item) => {
    return deliveryStatuses.find(
      (status) => Number(status?.id) === Number(item?.deliveryInfo?.status)
    );
  };

  const formatReservationType = (reservationType) => {
    // Find the reservation type by matching the value
    const reservationTypeEntry = Object.values(RESERVATIONS_TYPES).find(
      (type) => type.value === reservationType
    );
    return reservationTypeEntry?.text || '-';
  };

  const formatAvailabilityStatus = (availabilityStatus) => {
    // Find the availability status by matching the value
    const availabilityEntry = Object.values(AVAILABILITY_STATUSES).find(
      (status) => status.value === availabilityStatus
    );
    return availabilityEntry?.text || '-';
  };

  return data.map((item) => {
    const assignee = assigneesMap?.[item?.assignee] || 'None';
    const outOfSla = outOfSlaOptions?.filter((option) =>
      item?.outOfSla?.includes(option?.id)
    );

    return {
      createdAt: item.createdAt
        ? formatDate(item.createdAt, 'dd/MM/yyyy')
        : '-',
      updatedAt: item.updatedAt
        ? formatDate(item.updatedAt, 'dd/MM/yyyy')
        : '-',
      promisedDeliveryDate: item.promisedDeliveryDate
        ? formatDate(item.promisedDeliveryDate, 'dd/MM/yyyy HH:mm')
        : '',
      promisedDeliveryDateUntil: item.promisedDeliveryDateUntil
        ? formatDate(item.promisedDeliveryDateUntil, 'dd/MM/yyyy HH:mm')
        : '',
      endContractDate: item.endContractDate
        ? formatDate(item.endContractDate, 'dd/MM/yyyy')
        : '-',
      deliveryDate: item.deliveryDate
        ? formatDate(item.deliveryDate, 'dd/MM/yyyy')
        : '-',
      promisedReturnDate: item.promisedReturnDate
        ? formatDate(item.promisedReturnDate, 'dd/MM/yyyy')
        : '-',
      returnDate: item.returnDate
        ? formatDate(item.returnDate, 'dd/MM/yyyy')
        : '-',
      vehicle: item?.vehicle
        ? `${item?.vehicle?.manufacturer || ''} - ${item?.vehicle?.model || ''}`
        : '-',
      vehiclePlate: item?.vehicle?.plate || '-',
      amount: item?.rules?.[0]?.amount?.value
        ? formatAmount(item?.rules?.[0]?.amount?.value)
        : '-',
      pipedriveId: item?.pipedriveId || '-',
      pipedriveUrl: item?.pipedriveUrl,
      bookingAgent: item?.bookingAgent
        ? assigneesMap?.[item?.bookingAgent]?.display
        : '-',
      id: item.id,
      internal_id: item.internal_id,
      type: item.type,
      labels: item.labels || [],
      status: item.status,
      // as owner we define the subscriber who has subscription with role === 1 (owner)
      owner:
        item?.subscribers?.find((subscriber) =>
          subscriber?.subscriptions?.find(
            (subscription) =>
              subscription.role === 1 && subscription.id === item.id
          )
        )?.email || '-',
      carId: item?.vehicle?.id,
      vehicleInternalId: item?.vehicle?.internalId,
      deliveryStatus: deliveryStatus(item)?.name || '-',
      location:
        servicePointsMap[item?.vehicle?.location?.name]?.name ||
        item?.vehicle?.location?.name ||
        '-',
      stage: formatVehicleStage(item?.vehicle?.procurement?.currentStageId),
      reservationType: formatReservationType(
        item?.vehicle?.availability?.reservationType
      ),
      availabilityStatus: item?.vehicle?.availability?.status
        ? formatAvailabilityStatus(item?.vehicle.availability.status)
        : '-',
      plannedDownpaymentDate: item?.vehicle?.procurement?.plannedDownpaymentDate
        ? formatDate(
            item?.vehicle?.procurement?.plannedDownpaymentDate,
            'dd/MM/yyyy'
          )
        : '-',
      referralId: item?.referralId || '-',
      assignee,
      tin: item?.billingInfo?.vat || '-', // vat and tin are the same, product asked to rename vat to tin for the frontend
      isErfuddAlert: item?.isErfuddAlert || false,
      erfudd: item?.vehicle?.availability?.erfudd
        ? formatDate(item?.vehicle?.availability?.erfudd, 'dd/MM/yyyy')
        : '-',
      contractValue: formatCurrencyValue(item?.contractValue),
      outOfSla:
        outOfSla && Array.isArray(outOfSla)
          ? outOfSla.map((option) => option.name)
          : '-',
      customerTypes:
        item?.customerTypes?.map((type) => ({
          text: type.name,
          value: type.key,
        })) || [],
      bundles:
        item?.bundles
          ?.slice()
          ?.sort(
            (a, b) =>
              getBundleSortPriority(a?.status) -
              getBundleSortPriority(b?.status)
          )
          ?.map((bundle) => ({
            text: bundle?.type?.label || '-',
            value: generateShortId(),
            status: bundle?.status?.value && capitalize(bundle?.status?.value),
            colors: {
              ...getSubscriptionBundleColorStatus(bundle?.status?.value),
            },
          })) || [],
    };
  });
};

export const getAgentsRequestPayload = (payload = {}) => {
  const fields = omit(payload, ['page', 'pageSize']) || {};
  const multiFilterStringFields = ['role'];

  const getValue = ({ field }) => {
    if (multiFilterStringFields.includes(field)) {
      return payload[field]?.split(',') || [];
    }
    return payload[field];
  };
  const getType = ({ field }) => {
    if (multiFilterStringFields.includes(field)) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const value = [
    ...Object.keys(fields).map((field) => ({
      field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: getType({ field }),
      value: getValue({ field }),
    })),
  ];

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

export const handleDriversData = (data = []) => {
  if (!data.length) return [];

  return data
    ?.map((agent) => {
      return {
        text: `${agent?.firstName || ''} ${agent?.lastName || ''}`,
        value: agent?.email,
      };
    })
    ?.sort((a, b) => a.text.localeCompare(b.text));
};

export const handleAssigneesData = (data = []) => {
  if (!data.length) return [];

  const acceptedAssigneesByRole = data?.filter((assignee) =>
    ACCEPTABLE_SUBSCRIPTION_AGENT_ROLES.includes(assignee?.role)
  );

  return [{ text: 'None', value: 'none' }, ...acceptedAssigneesByRole];
};

export const handleSubscriptionBundlesData = (data = []) => {
  if (!data.length) return [];

  return data.map((bundle) => ({
    ...bundle,
    createdAt: bundle.createdAt
      ? formatDate(bundle.createdAt, 'dd/MM/yyyy')
      : '-',
    endsAt: bundle.endsAt ? formatDate(bundle.endsAt, 'dd/MM/yyyy') : '-',
  }));
};

export const handleChangeLog = ({ changelog, getAssigneeName }) => {
  const renderValue = (field, value) => {
    if (isNil(value) || value === '') return 'empty';

    const normalizedField = Array.isArray(field)
      ? field.join('.')
      : String(field || '');

    if (
      normalizedField.toLowerCase().includes('date') &&
      isValid(new Date(value))
    )
      return formatDate(value, 'dd/MM/yyyy HH:mm');
    return String(value);
  };

  return changelog.map((log) => {
    if (log.field.includes('Assignee')) {
      return {
        ...log,
        actor: getAssigneeName(log.actor) || log.actor,
        boardType: 'changelog',
        type: 'changelog',
        fieldName: log.field.join('.'),
        valueFrom: log.from ? getAssigneeName(log.from) : 'empty',
        valueTo: log.to ? getAssigneeName(log.to) : 'empty',
      };
    }
    return {
      ...log,
      actor: getAssigneeName(log.actor) || log.actor,
      boardType: 'changelog',
      type: 'changelog',
      fieldName: log.field.join('.'),
      valueFrom: renderValue(log.field, log.from),
      valueTo: renderValue(log.field, log.to),
    };
  });
};

export const handleEmailHistory = ({ emailHistory, getAssigneeName }) => {
  return emailHistory.map((email) => {
    const trimmedBody =
      email?.body?.length > 100
        ? `${email.body.slice(0, 100)}...`
        : email?.body;

    return {
      ...email,
      actor: getAssigneeName(email.actor) || email.actor,
      status: email.status,
      type: AVAILABLE_HISTORY_ITEMS.email,
      subject: email.subject || '(no subject)',
      sender: email?.from?.name || email?.from?.emailAddress,
      snippet: email?.snippet || trimmedBody || '(no body)',
      body: email?.body || '(no body)',
      receivers: email?.to?.map((recipient) => ({
        name: recipient?.name || recipient?.emailAddress,
        email: recipient?.emailAddress,
        id: recipient?.id,
      })),
      assignee: email?.assignee || email?.assigneeEmail,
    };
  });
};

export const handleSubscriptionStatusesOptions = (
  deliveryStatus,
  subscriptionStatusOptions
) => {
  if (!subscriptionStatusOptions?.length) return [];

  let normalizedDeliveryStatus = null;
  if (typeof deliveryStatus === 'number') {
    normalizedDeliveryStatus = deliveryStatus;
  } else if (
    typeof deliveryStatus === 'string' &&
    deliveryStatus.trim() !== ''
  ) {
    const parsed = Number(deliveryStatus);
    normalizedDeliveryStatus = Number.isNaN(parsed) ? null : parsed;
  }

  const shouldCheckDelivery =
    normalizedDeliveryStatus !== null &&
    !Number.isNaN(normalizedDeliveryStatus);
  const isDeliveryCompleted =
    shouldCheckDelivery &&
    normalizedDeliveryStatus === DELIVERY_INFO_STATUSES_ENUM.DELIVERY_COMPLETED;
  return subscriptionStatusOptions.map((option) => {
    const shouldDisable =
      shouldCheckDelivery &&
      !isDeliveryCompleted &&
      (option.value === SUBSCRIPTIONS_STATUSES_ENUM.ACTIVE ||
        option.value === SUBSCRIPTIONS_STATUSES_ENUM.EXPIRING ||
        option.value === SUBSCRIPTIONS_STATUSES_ENUM.RENEWAL ||
        option.value === SUBSCRIPTIONS_STATUSES_ENUM.FOR_RETURN ||
        option.value === SUBSCRIPTIONS_STATUSES_ENUM.ENDED);

    return {
      ...option,
      disabled: shouldDisable,
      description: shouldDisable
        ? 'Cannot pick until delivery status is completed'
        : '',
    };
  });
};

export const getSubscriptionsQueryParamsAndPayload = ({
  routerQuery,
  page,
  pageSize,
  orderBy,
  status,
  type,
  order,
  query,
  deliveryStatus,
  deliveryLocation,
  availabilityStatus,
  assignee,
  isErfuddAlert,
  filters,
}) => {
  const params = getCleanedUrlParams({
    ...routerQuery,
    page,
    pageSize,
    orderBy,
    status,
    type,
    order,
    query,
    deliveryStatus,
    deliveryLocation,
    availabilityStatus,
    assignee,
    isErfuddAlert,
  });

  const cleanedParams = getSubscriptionsQueryParams(params);

  const payload = getSubscriptionsRequestPayload(
    params,
    filters.modalFiltersRules
  );

  return {
    cleanedParams,
    payload,
  };
};

export const handleSubscriptionComments = ({
  data,
  loggedInUser,
  getAssigneeName,
}) => {
  if (!data?.length) return [];

  return (
    data?.map((item) => {
      return {
        id: item.id,
        type: 'comment',
        comment: item.comment,
        createdAt: item.createdAt,
        createdBy: item.author,
        userName: getAssigneeName(item.author),
        isLoggedInUser: item.author === loggedInUser?.email,
      };
    }) || []
  );
};

const normalizeEmailReceivers = (itemData) => {
  const hasReceivers = itemData?.receivers?.length;
  const list = hasReceivers ? itemData.receivers : itemData?.emailReceivers;

  if (!list?.length) return [];

  return list.map((receiver) => {
    const normalized = {
      id: receiver?.id || '',
      name: receiver?.name || '',
      email: receiver?.email || '',
    };

    return hasReceivers ? { ...receiver, ...normalized } : normalized;
  });
};

export const handleInfiniteQuerySubscriptionData = ({
  data,
  loggedInUser,
  handleChangelogItem,
  isSystemActor,
  getAssigneeName,
}) => {
  if (!data || !data?.length) return [];

  return data.map((item) => {
    const type = item?.type;

    if (type === AVAILABLE_HISTORY_ITEMS.changelog) {
      const itemData = item?.data || {};

      const changelogInitialItem = {
        ...itemData,
        fieldName: itemData?.field ?? itemData?.fieldName,
        valueFrom: itemData?.from ?? itemData?.valueFrom,
        valueTo: itemData?.to ?? itemData?.valueTo,
      };

      const changelogItem = handleChangelogItem(changelogInitialItem);

      const changelogActor = changelogItem?.actor || '';

      const actor = isSystemActor(changelogActor)
        ? changelogActor
        : getAssigneeName(changelogActor);

      return {
        type,
        ...itemData,
        ...(changelogItem || {}),
        actor,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.sms) {
      const boardType = item?.data?.boardType;

      const boardMeta = BOARD_META_BY_ID[boardType] || {};

      const resourceId = item?.data?.resourceId || '';

      const ticketUrl =
        boardMeta.url && resourceId ? `${boardMeta.url}/${resourceId}` : null;
      const sender = getAssigneeName(item?.data?.sender);
      return {
        ...(item.data || {}),
        type,
        sender,
        url: ticketUrl,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.comment) {
      const itemData = item?.data || {};

      return {
        type,
        ...itemData,
        isLoggedInUser: itemData?.author === loggedInUser?.email,
        userName: getAssigneeName(itemData.author),
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.email) {
      const itemData = item?.data || {};

      const url =
        itemData?.threadId && itemData?.mailboxId
          ? `${URLS.communication}/${itemData?.threadId}?mailboxId=${itemData?.mailboxId}`
          : '';

      const status = itemData?.status || '';

      const sender =
        `${itemData?.senderFirstName || ''} ${itemData?.senderLastName || ''} ${
          itemData?.emailSender?.name
        }`.trim() || '-';

      const subject = itemData?.subject || '';

      return {
        ...itemData,
        url,
        type,
        status,
        subject,
        sender,
        receivers: normalizeEmailReceivers(itemData),
      };
    }

    return {
      type,
      ...(item?.data || {}),
    };
  });
};

export const handleSMS = ({ sms, getAssigneeName }) => {
  if (!sms?.length) return [];

  return (
    sms?.map((item) => ({
      createdAt: item?.CreatedAt,
      message: item?.Message || '',
      receiver: item?.SentTo || '',
      type: 'sms',
      sender: getAssigneeName(item?.CreatedBy),
    })) || []
  );
};
